import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAction } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { useAuth } from "../../hooks/useAuth";
import { getOnboardingContext } from "../../lib/onboardingStorage";
import { savePendingPurchaseContext } from "../../lib/pendingPurchase";
import {
  getBrowserBillingCurrency,
  getLocalPriceFallback,
  WEB_PLAN_PRICES,
} from "../../lib/localizedPricing";
import { t } from "../../i18n";
import { withTimeout } from "../../lib/withTimeout";

export function OnboardingPaywallPage() {
  const [searchParams] = useSearchParams();
  const { isAuthenticated, createAnonymousSession } = useAuth();
  const createPurchaseLink = useAction(api.stripe.createWebCheckoutLink);
  const billingCurrency = getBrowserBillingCurrency();

  const handle = searchParams.get("handle") ?? "";
  const ctx = getOnboardingContext();
  const previewData = ctx.previewData;
  const previewSessionId = ctx.previewSessionId ?? "";

  const [selectedPlan, setSelectedPlan] = useState<"weekly" | "monthly">(
    "monthly",
  );
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const followings = previewData?.followings ?? [];
  const femaleCount = previewData?.femaleCount ?? 0;
  const maleCount = previewData?.maleCount ?? 0;

  const handleSubscribe = async () => {
    setErrorMsg(null);
    setActionLoading(true);

    try {
      await withTimeout(
        (async () => {
          if (!isAuthenticated) {
            await createAnonymousSession();
          }

          savePendingPurchaseContext({
            source: "onboarding_paywall",
            handle,
            previewSessionId,
          });

          const result = await createPurchaseLink({
            priceLookupKey: selectedPlan,
            currency: billingCurrency,
            handle,
            previewSessionId,
            returnTo: "paywall_success",
          });

          window.location.assign(result.url);
        })(),
      );
    } catch (err: any) {
      console.error("Failed to create checkout session", err);
      setErrorMsg(t("paywallErrorStartMonitoring"));
    } finally {
      setActionLoading(false);
    }
  };

  const planPrices = WEB_PLAN_PRICES[billingCurrency];
  const weeklyPrice = getLocalPriceFallback(planPrices.weekly, billingCurrency);
  const monthlyPrice = getLocalPriceFallback(planPrices.monthly, billingCurrency);
  const weeklyPeriod = t("paywallBillingPeriodWeekly");
  const monthlyPeriod = t("paywallBillingPeriodMonthly");
  const perDayPrice = getLocalPriceFallback(
    planPrices.monthly / 30,
    billingCurrency,
  );
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #fdf2f2 0%, #ffffff 100%)",
        padding: "24px",
      }}
    >
      <div style={{ maxWidth: "420px", margin: "0 auto" }}>
        {/* Header */}
        <div
          style={{
            textAlign: "center",
            marginBottom: "24px",
            paddingTop: "32px",
          }}
        >
          <div style={{ fontSize: "36px", marginBottom: "12px" }}>🔒</div>
          <h1
            style={{
              fontSize: "1.375rem",
              fontWeight: 800,
              color: "#171717",
              marginBottom: "6px",
            }}
          >
            {t("paywallTitleTarget", { handle })}
          </h1>
          <p
            style={{ fontSize: "0.9375rem", color: "#737373", lineHeight: 1.5 }}
          >
            {t("paywallSubtitleTarget", { handle })}
          </p>
        </div>

        {/* Preview context */}
        {followings.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "12px",
              marginBottom: "24px",
            }}
          >
            {femaleCount > 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "10px 16px",
                  background: "#fdf2f2",
                  borderRadius: "12px",
                  border: "1px solid rgba(255,107,138,0.15)",
                }}
              >
                <span style={{ fontSize: "20px", display: "block" }}>👩</span>
                <strong style={{ fontSize: "1rem", color: "#e84a6f" }}>
                  {femaleCount}
                </strong>
                <div style={{ fontSize: "0.625rem", color: "#e84a6f" }}>
                  girls
                </div>
              </div>
            )}
            {maleCount > 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "10px 16px",
                  background: "#eff6ff",
                  borderRadius: "12px",
                  border: "1px solid #dbeafe",
                }}
              >
                <span style={{ fontSize: "20px", display: "block" }}>👨</span>
                <strong style={{ fontSize: "1rem", color: "#1d4ed8" }}>
                  {maleCount}
                </strong>
                <div style={{ fontSize: "0.625rem", color: "#1d4ed8" }}>
                  guys
                </div>
              </div>
            )}
          </div>
        )}

        {/* Plan cards */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            marginBottom: "16px",
          }}
        >
          {/* Weekly */}
          <div
            onClick={() => setSelectedPlan("weekly")}
            style={{
              padding: "16px",
              background: selectedPlan === "weekly" ? "white" : "#fafafa",
              border: `2px solid ${selectedPlan === "weekly" ? "#ff6b8a" : "rgba(15,23,42,0.08)"}`,
              borderRadius: "16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "0.9375rem",
                  color: "#171717",
                }}
              >
                {t("paywallPlanWeekly")}
              </div>
              <div style={{ fontSize: "0.75rem", color: "#a3a3a3" }}>
                {t("paywallPlanWeeklyDesc")}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontWeight: 800,
                  fontSize: "1.125rem",
                  color: "#171717",
                }}
              >
                {weeklyPrice}
              </div>
              <div style={{ fontSize: "0.6875rem", color: "#a3a3a3" }}>
                /{weeklyPeriod}
              </div>
            </div>
          </div>

          {/* Monthly */}
          <div
            onClick={() => setSelectedPlan("monthly")}
            style={{
              padding: "16px",
              background: selectedPlan === "monthly" ? "white" : "#fafafa",
              border: `2px solid ${selectedPlan === "monthly" ? "#ff6b8a" : "rgba(15,23,42,0.08)"}`,
              borderRadius: "16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "-10px",
                right: "16px",
                background: "#22c55e",
                color: "white",
                fontSize: "0.6875rem",
                fontWeight: 700,
                padding: "2px 10px",
                borderRadius: "6px",
              }}
            >
              {t("paywallBestValue")}
            </div>
            <div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "0.9375rem",
                  color: "#171717",
                }}
              >
                {t("paywallPlanMonthly")}
              </div>
              <div style={{ fontSize: "0.75rem", color: "#a3a3a3" }}>
                {t("paywallPlanMonthlyDesc")}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontWeight: 800,
                  fontSize: "1.125rem",
                  color: "#171717",
                }}
              >
                {monthlyPrice}
              </div>
              <div style={{ fontSize: "0.6875rem", color: "#a3a3a3" }}>
                /{monthlyPeriod}
              </div>
            </div>
          </div>
        </div>

        <p
          style={{
            textAlign: "center",
            color: "#a3a3a3",
            fontSize: "0.75rem",
            marginBottom: "16px",
          }}
        >
          {t("paywallPerDay", { price: perDayPrice })} •{" "}
          {t("paywallCancelAnytime")}
        </p>

        {errorMsg && (
          <div
            style={{
              padding: "12px",
              background: "#fef2f2",
              color: "#ef4444",
              borderRadius: "12px",
              fontSize: "0.8125rem",
              marginBottom: "12px",
              textAlign: "center",
            }}
          >
            {errorMsg}
          </div>
        )}

        {/* CTA */}
        <button
          onClick={handleSubscribe}
          disabled={actionLoading}
          style={{
            width: "100%",
            padding: "16px",
            background: actionLoading
              ? "#ffb1b8"
              : "var(--pink-primary, #ff6b8a)",
            color: "white",
            border: "none",
            borderRadius: "16px",
            fontWeight: 800,
            fontSize: "1.0625rem",
            cursor: actionLoading ? "not-allowed" : "pointer",
            boxShadow: "0 4px 16px rgba(255, 107, 138, 0.3)",
            marginBottom: "8px",
          }}
        >
          {actionLoading ? t("paywallActivatingPlan") : t("paywallCta")}
        </button>

        <p
          style={{
            textAlign: "center",
            fontSize: "0.6875rem",
            color: "#a3a3a3",
          }}
        >
          {t("paywallTerms")}
        </p>
      </div>
    </div>
  );
}
